import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog'; // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { GrowlerService } from '../../../../shared/growler/growler.service';
import { PayablesService } from '../../../../../services/payables/payables.service';
import { Payable } from '../../../../../models/entities/payable';
import { LegFiJwtService } from '../../../../../services/auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../../../../../services/auth/jwt-legfi-claims.model';
import { PayableActionType, PaymentMethod, PaymentSchedule } from '../../../../../enums/payables/payable-action.enum';
import { Organization } from '../../../../../models/entities/organization';
import { OrganizationService } from '../../../../../services/organization/organization.service';
import { LobBankAccount } from '../../../../../models/entities/lob-bank-account';
import { VendorBankAccount } from '../../../../../models/entities/vendors/vendor-bank-account';
import { VENDOR_CHECK_NAME_TOO_LONG_HINT, vendorNameTooLongForCheck } from '../../../../../models/entities/vendors/vendor';
import { BillPayService } from '../../../../../services/members/bill-pay.service';
import { BankAccountInsightsService } from '../../../../../services/financial-accounts/bank-account-insights.service';
import { VendorBankAccountsService } from '../../../../../services/stripe-treasury/vendor-bank-accounts.service';
import { DateTimeUtilsService } from '../../../../../services/datetime-utils.service';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GenericDialogResult } from '../../../shared/generic-mat-dialog/generic-dialog-result';
import { environment } from 'environments/environment';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { PayableReviewComponent } from '../../payable-review/payable-review.component';
import { LobBankAccountSelectorComponent } from '../../../accounting/lob-bank-account-selector/lob-bank-account-selector.component';
import { CheckNumberInputComponent } from '../../../../shared/check-number-input/check-number-input.component';
import { ControlMessagesComponent } from '../../../../shared/control-messages/control-messages.component';
import { awaitsSecondUnderwriting, TreasuryService, TreasuryUnderwritingStatus } from '../../../../../services/stripe-treasury/treasury.service';
import { LegfiCurrencyPipe } from '../../../../../pipes/format/legfi-currency.pipe';
import { ApprovalRequiredDialogComponent } from '../../../../v2-layout/stripe-treasury/dialogs/approval-required-dialog/approval-required-dialog.component';

/**
 * Why an option is unusable. Each maps to its own copy in the template, because most of the
 * reasons carry a link the user has to follow to fix it.
 */
export type PaymentMethodHint = 'zeroAmount' | 'verifyBillPay' | 'vendorNameTooLong' | 'vendorAch' | 'vendorAchUnreadable' | 'vendorAchUnavailable' | 'achExceedsBalance' | 'achSwitchedOff';

export interface PaymentMethodOption {
    value: PaymentMethod;
    display: string;
    visible: boolean;
    disabled: boolean;
    hint: PaymentMethodHint | null;
}

@UntilDestroy()
@Component({
    selector: 'app-pay-invoice-dialog',
    templateUrl: './pay-invoice-dialog.component.html',
    styleUrls: ['./pay-invoice-dialog.component.scss'],
    standalone: true,
    imports: [
        ApprovalRequiredDialogComponent,
        CommonModule,
        ReactiveFormsModule,
        PayableReviewComponent,
        LobBankAccountSelectorComponent,
        CheckNumberInputComponent,
        ControlMessagesComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PayInvoiceDialogComponent implements OnInit, OnDestroy {
    /**
     * The picker's floor and default, as `YYYY-MM-DD`: the ORGANIZATION's today, not the browser's.
     * `whenToPayForSelectedDate()` compares the picked date against this same org-zone today, so an
     * ACH transfer scheduled off the browser's clock could otherwise be held (or unreachable as
     * IMMEDIATE) across a zone gap.
     */
    minDate: string = this._dateTimeUtils.formatForApi(this._dateTimeUtils.todayInOrgTimezone());
    isLoadingPaymentMethod: boolean = false;
    isSubmitting: boolean = false;
    jwt: JwtLegFiClaims;
    organization: Organization;
    lobBankAccounts: LobBankAccount[] = [];
    vendorBankAccounts: VendorBankAccount[] = [];

    /**
     * Why the vendor's bank accounts came back empty when the read itself failed. Without it an
     * empty list reads as "this vendor has no ACH account" and the dialog tells the user to enable
     * ACH in vendor settings — an instruction that fixes neither case:
     * - `forbidden`: the list endpoint needs FINANCIAL_ACCOUNTS access, so a bill-pay-only member
     *   gets a 403. Someone with that access has to send the payment.
     * - `unavailable`: any other failure (server error, dropped connection). Retrying is the fix,
     *   and sending the user to find an admin would not be.
     */
    vendorBankAccountsError: 'forbidden' | 'unavailable' | null = null;

    /**
     * The verified subset of `lobBankAccounts`, held as a FIELD and recomputed only when the
     * accounts load. It must not be a getter: the template binds it into
     * `<app-check-number-input [bankAccounts]>`, and a getter returns a new array on every call,
     * so Angular's reference comparison fires `ngOnChanges` on every change-detection cycle.
     * `CheckNumberInputComponent` refills the check number from that hook, which re-filled the
     * field the instant a user cleared it to type a different number.
     */
    verifiedBillPayAccounts: LobBankAccount[] = [];

    paymentMethodOptions: PaymentMethodOption[] = [];
    hasSelectableMethod: boolean = false;

    /**
     * SD-9900 / SD-10162: the org's Treasury second-underwriting read, gating ACH exactly as the
     * create-payable dialog does. Held back only by a review in progress, ACH stays selectable and
     * Confirm answers with the Approval Required dialog; switched off for any other reason, ACH is
     * disabled. Null (not read or unavailable) gates nothing — the backend gate is authoritative.
     */
    underwritingStatus: TreasuryUnderwritingStatus | null = null;

    /** SD-10162: the Treasury available balance ACH pays from, in cents. Null until read. */
    treasuryAvailableCents: number | null = null;

    paymentForm = new UntypedFormGroup({
        paymentMethod: new UntypedFormControl(null, [
            Validators.required,
        ]),
        scheduledPaymentDate: new UntypedFormControl(this.minDate, [
            Validators.required,
            (control) => {
                if (!control.value) { return null; }
                // Compared as CALENDAR DATES against the ORG's today, for the same reason `minDate`
                // is built from it — the browser's day is the wrong yardstick across a zone gap.
                const selectedDate = this._dateTimeUtils.formatForApi(control.value);
                return selectedDate < this.organizationToday() ? { pastDate: 'Payment date cannot be in the past' } : null;
            }
        ]),
        bankAccountId: new UntypedFormControl({value: null, disabled: true}),
        checkNumber: new UntypedFormControl({value: null, disabled: true}, [
            Validators.min(1),
            Validators.max(500000000)
        ]),
        vendorBankAccountId: new UntypedFormControl({value: null, disabled: true})
    });
    lobMailCheckFee: string;

    private bankAccountAutoSelectTimeout: ReturnType<typeof setTimeout> | null = null;
    private readonly _titleCasePipe = new TitleCasePipe();
    private readonly _currency = new LegfiCurrencyPipe();

    /** SD-10162: the Approval Required dialog, shown when a review still under way is all that stands in the way. */
    approvalRequiredOpen = false;

    constructor(
            private _dialogRef: MatDialogRef<PayInvoiceDialogComponent>, // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
            private _growlerService: GrowlerService,
            private _payableService: PayablesService,
            private _organizationService: OrganizationService,
            private _billPayService: BillPayService,
            private _bankAccountInsights: BankAccountInsightsService,
            private _vendorBankAccountsService: VendorBankAccountsService,
            private _router: Router,
            private _treasuryService: TreasuryService,
            private _dateTimeUtils: DateTimeUtilsService,
            @Inject(MAT_DIALOG_DATA) public data: { // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
                payable: Payable
            },
    ) {
        this._dialogRef.addPanelClass('kit-dialog');
    }

    get paymentMethod() { return this.paymentForm.get('paymentMethod'); }
    get scheduledPaymentDate() { return this.paymentForm.get('scheduledPaymentDate'); }
    get bankAccount() { return this.paymentForm.get('bankAccountId'); }
    get vendorBankAccount() { return this.paymentForm.get('vendorBankAccountId'); }

    get hasPayabliIntegration(): boolean {
        const { id } = this.organization;
        const { DemoOrganizationId, AdminTestGroupOrganizationId, production } = environment;

        return this.organization.profile.hasApprovedPayabliApplication ||
                (production && [DemoOrganizationId, AdminTestGroupOrganizationId].includes(id));
    }

    get hasVerifiedBillPayAccount(): boolean {
        return this.verifiedBillPayAccounts.length > 0;
    }

    get showMailCheckOptions(): boolean {
        return this.paymentMethod?.value === PaymentMethod.MAIL_CHECK;
    }

    get showAchOptions(): boolean {
        return this.paymentMethod?.value === PaymentMethod.ACH_TRANSFER;
    }

    get hasTreasuryAccount(): boolean {
        return this._bankAccountInsights.hasTreasuryAccount(this.organization?.depositBankAccounts ?? []);
    }

    get isVendorAchEnabled(): boolean {
        return this.vendorBankAccounts.length > 0;
    }

    get isZeroAmount(): boolean {
        return this.data.payable.amount === 0;
    }

    /** SD-10162: outbound transfers are held back only by second underwriting still under way. */
    get achAwaitsApproval(): boolean {
        return awaitsSecondUnderwriting(this.underwritingStatus, 'outboundTransfersEnabled');
    }

    /** Outbound transfers are off for a reason other than a review in progress. */
    get achSwitchedOff(): boolean {
        return this.underwritingStatus !== null
            && !this.underwritingStatus.outboundTransfersEnabled
            && !this.achAwaitsApproval;
    }

    /** SD-10162: the payable is more than the Treasury balance can pay. */
    get achExceedsBalance(): boolean {
        return this.treasuryAvailableCents !== null && this.data.payable.amount > this.treasuryAvailableCents;
    }

    get selectedAchBankAccount(): VendorBankAccount {
        return this.vendorBankAccounts.find(account => account.id === this.vendorBankAccount?.value);
    }

    /** The flat {value,label,disabled} data `ph-radio-group` renders; disabled reasons live in the hints below it. */
    get paymentMethodItems(): { value: PaymentMethod; label: string; disabled: boolean; detail?: string }[] {
        return this.paymentMethodOptions
                .filter(option => option.visible)
                .map(option => ({
                    value: option.value,
                    label: option.display,
                    disabled: option.disabled,
                    ...(option.hint === 'vendorNameTooLong' ? { detail: VENDOR_CHECK_NAME_TOO_LONG_HINT } : {}),
                }));
    }

    get vendorNameTooLongForCheck(): boolean {
        return vendorNameTooLongForCheck(this.data?.payable?.vendor);
    }

    get vendorBankAccountItems(): { value: number; label: string }[] {
        return this.vendorBankAccounts.map(account => ({
            value: account.id,
            label: `${account.accountHolderName} — ${this._titleCasePipe.transform(account.accountType)} • ${account.last4}`,
        }));
    }

    ngOnInit(): void {
        this.jwt = LegFiJwtService.read();
        if (this.data.payable.scheduledPaymentDate) {
            // The entity already parses this as a Moment (`Payable`: `moment(str, 'YYYY-MM-DD')`), so
            // formatting it (never mutating it) keeps the same calendar day as a plain `YYYY-MM-DD` string.
            this.scheduledPaymentDate.setValue(this._dateTimeUtils.formatForApi(this.data.payable.scheduledPaymentDate));
        }

        // Set before the load: the mail-check option's label carries the fee, and the options are
        // built as soon as the load resolves.
        this.lobMailCheckFee = this._payableService.getFormattedLobMailCheckFee();
        this.loadInitialData();
    }

    ngOnDestroy(): void {
        this.clearBankAccountAutoSelectTimeout();
    }

    trackByOptionValue(_index: number, option: PaymentMethodOption): PaymentMethod {
        return option.value;
    }

    trackByVendorBankAccountId(_index: number, account: VendorBankAccount): number {
        return account.id;
    }

    navigateToVendorSettings(event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();
        const vendorId = this.data.payable.vendorId;
        if (vendorId) {
            this._dialogRef.close();
            this._router.navigate(['/app/vendor/details', vendorId]);
        }
    }

    confirmAndSchedulePayment(): void {
        if (this.isSubmitting) {
            return;
        }

        if (this.paymentForm.invalid) {
            this.paymentForm.markAllAsTouched();
            return this._growlerService.oops('Please fill in all required fields');
        }

        const paymentMethod = this.paymentMethod.value;

        // SD-10162: ACH the balance covers (the option is disabled otherwise), held back only by
        // second underwriting still under way.
        if (paymentMethod === PaymentMethod.ACH_TRANSFER && this.achAwaitsApproval) {
            this.approvalRequiredOpen = true;
            return;
        }

        // Built key by key rather than spread from getRawValue(): that reads THROUGH disabled
        // controls, which is how a mail check the user never picked used to reach the API.
        const payload = {
            organizationId: this.jwt.orgId,
            payableAction: {
                invoiceAction: PayableActionType.SCHEDULE as const,
                paymentMethod: paymentMethod,
                paymentDate: this._dateTimeUtils.formatForApi(this.scheduledPaymentDate.value),
                ...(paymentMethod === PaymentMethod.MAIL_CHECK ? {
                    bankAccountId: this.bankAccount.value,
                    checkNumber: this.paymentForm.get('checkNumber').value,
                } : {}),
                ...(paymentMethod === PaymentMethod.ACH_TRANSFER ? {
                    vendorBankAccountId: this.vendorBankAccount.value,
                    whenToPay: this.whenToPayForSelectedDate(),
                } : {}),
            },
        };

        this.isSubmitting = true;
        this._payableService.updatePayablePaymentMethodPostApproval(this.data.payable.id, payload)
                .pipe(
                        finalize(() => this.isSubmitting = false),
                        untilDestroyed(this)
                )
                .subscribe({
                    next: () => {
                        this._growlerService.success('Success', 'Payable updated successfully');
                        this._dialogRef.close(<GenericDialogResult>{ success: true });
                    },
                    error: () => {
                        this._growlerService.oops('Error updating payable');
                    },
                });
    }

    cancel() {
        this._dialogRef.close();
    }

    /**
     * An ACH transfer scheduled for the org's current day is sent immediately; anything later is
     * held until that date.
     *
     * Both sides are compared as CALENDAR DATES, not instants: the picker holds a local date-only
     * value, and the org's "today" is a date in the org's timezone. Comparing the two as moments
     * (`isSame(..., 'day')`) measures an org-timezone instant against the browser's local day
     * window, which lands on the wrong side of midnight whenever the two zones differ — and
     * `paymentDate` in the same payload is already the formatted calendar date.
     */
    /** The organization's current calendar date, `YYYY-MM-DD`, read in the org's zone explicitly. */
    private organizationToday(): string {
        return this._dateTimeUtils.formatForApi(this._dateTimeUtils.todayInOrgTimezone());
    }

    private whenToPayForSelectedDate(): PaymentSchedule {
        const orgToday = this.organizationToday();
        const selectedDate = this._dateTimeUtils.formatForApi(this.scheduledPaymentDate.value);

        return selectedDate === orgToday
                ? PaymentSchedule.IMMEDIATE
                : PaymentSchedule.NO_EARLIER_THAN;
    }

    private loadInitialData(): void {
        this.isLoadingPaymentMethod = true;

        forkJoin({
            organization: this._organizationService.getOrganizationById(this.jwt.orgId, ['profile', 'depositBankAccounts']),
            accounts: this._billPayService.getLobBankAccountsForOrganization(this.jwt.orgId),
            // ACH availability must not be able to take the whole dialog down with it. Reading vendor
            // bank accounts needs FINANCIAL_ACCOUNTS read access (ListVendorBankAccountsRequest), so
            // a billpay-only member gets a 403 here — and an unhandled one would leave the payment
            // methods blank rather than just hiding ACH.
            vendorBankAccounts: this._vendorBankAccountsService.list(this.jwt.orgId, this.data.payable.vendorId)
                .pipe(catchError((error) => {
                    console.warn('[PayInvoiceDialog] SD-10055 could not read vendor bank accounts; ACH will be unavailable', error);
                    this.vendorBankAccountsError = error?.status === 403 ? 'forbidden' : 'unavailable';

                    return of([]);
                }))
        }).pipe(
                switchMap(({ organization, accounts, vendorBankAccounts }) => {
                    this.organization = organization;
                    this.lobBankAccounts = accounts;
                    this.verifiedBillPayAccounts = accounts.filter(account => account.verified);
                    this.vendorBankAccounts = vendorBankAccounts;

                    // Only an org with a Treasury account can see the ACH option, so only then do the
                    // underwriting status and balance matter (create-payable-dialog does the same).
                    return this.hasTreasuryAccount
                        ? this.loadTreasuryGate()
                        : of({ status: null, availableCents: null });
                }),
                finalize(() => this.isLoadingPaymentMethod = false),
                untilDestroyed(this)
        ).subscribe({
            next: ({ status, availableCents }) => {
                this.underwritingStatus = status;
                this.treasuryAvailableCents = availableCents;
                this.setupPaymentMethodWatcher();
                this.buildPaymentMethodOptions();
            },
            error: () => this._growlerService.oops('Error loading data')
        });
    }

    /**
     * SD-9900 / SD-10162: read what the ACH option is gated on — the org's second-underwriting status
     * and the Treasury balance it pays from. Either failed read resolves to null and gates nothing:
     * the backend refuses an ACH payment it cannot make.
     */
    private loadTreasuryGate(): Observable<{ status: TreasuryUnderwritingStatus | null; availableCents: number | null }> {
        return forkJoin({
            status: this._treasuryService.getUnderwritingStatus(this.jwt.orgId),
            availableCents: this._treasuryService.getBalance(this.jwt.orgId).pipe(
                    map(response => response?.balance?.available ?? null),
                    catchError(() => of(null)),
            ),
        });
    }

    /**
     * Payment method options, in the same order and on the same terms as the create-payable dialog.
     * No method is ever selected on the user's behalf — an option the org cannot use is rendered
     * disabled with the reason, and when nothing is usable the form blocks submission instead.
     */
    private buildPaymentMethodOptions(): void {
        this.paymentMethodOptions = [
            {
                value: PaymentMethod.VENDOR_DECIDE,
                display: 'Let the vendor decide',
                visible: this.hasPayabliIntegration,
                disabled: this.isZeroAmount,
                hint: this.isZeroAmount ? 'zeroAmount' : null,
            },
            {
                value: PaymentMethod.ACH_TRANSFER,
                // SD-10162: names the balance it pays from, once read.
                display: this.treasuryAvailableCents === null
                        ? 'ACH transfer (direct from Treasury Balance)'
                        : `ACH transfer (direct from Treasury Balance of ${this._currency.transform(this.treasuryAvailableCents, 'USD', 'symbol', '.2')})`,
                visible: this.hasTreasuryAccount,
                disabled: !this.isVendorAchEnabled || this.achSwitchedOff || this.isZeroAmount || this.achExceedsBalance,
                // Same precedence as the create dialog, with the vendor-setup step split in two:
                // "we checked and there is no account" and "we could not check" need different
                // instructions.
                hint: this.achHint(),
            },
            {
                value: PaymentMethod.MAIL_CHECK,
                display: `Mail a check (${this.lobMailCheckFee} will be charged to your PayHOA account)`,
                visible: true,
                disabled: !this.hasVerifiedBillPayAccount || this.isZeroAmount || this.vendorNameTooLongForCheck,
                hint: !this.hasVerifiedBillPayAccount ? 'verifyBillPay'
                    : (this.isZeroAmount ? 'zeroAmount' : (this.vendorNameTooLongForCheck ? 'vendorNameTooLong' : null)),
            },
        ];

        this.hasSelectableMethod = this.paymentMethodOptions.some(option => option.visible && !option.disabled);
    }

    /** Why ACH is unusable, if it is. A review still under way is not a reason: Confirm answers that. */
    private achHint(): PaymentMethodHint | null {
        if (!this.isVendorAchEnabled) {
            return this.vendorAchHint();
        }

        if (this.achSwitchedOff) {
            return 'achSwitchedOff';
        }

        if (this.isZeroAmount) {
            return 'zeroAmount';
        }

        return this.achExceedsBalance ? 'achExceedsBalance' : null;
    }

    /**
     * Which "no vendor ACH" copy to show. "We checked and there is no account", "we were not
     * allowed to check" and "the check failed" each need a different instruction.
     */
    private vendorAchHint(): PaymentMethodHint {
        switch (this.vendorBankAccountsError) {
            case 'forbidden':
                return 'vendorAchUnreadable';
            case 'unavailable':
                return 'vendorAchUnavailable';
            default:
                return 'vendorAch';
        }
    }

    private setupPaymentMethodWatcher(): void {
        this.paymentMethod.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            if (value === PaymentMethod.MAIL_CHECK) {
                this.bankAccount.enable();
                this.bankAccount.setValidators([Validators.required]);

                const verifiedAccounts = this.verifiedBillPayAccounts;
                if (verifiedAccounts.length === 1 && !this.bankAccount.value) {
                    this.clearBankAccountAutoSelectTimeout();
                    this.bankAccountAutoSelectTimeout = setTimeout(() => {
                        this.bankAccount.setValue(verifiedAccounts[0].id);
                    }, 0);
                }
                this.paymentForm.get('checkNumber').enable();
            } else {
                this.bankAccount.disable();
                this.bankAccount.setValue(null);
                this.paymentForm.get('checkNumber').disable();
                this.paymentForm.get('checkNumber').setValue(null);
            }

            if (value === PaymentMethod.ACH_TRANSFER) {
                this.vendorBankAccount.enable();
                this.vendorBankAccount.setValidators([Validators.required]);
                if (!this.vendorBankAccount.value && this.vendorBankAccounts.length > 0) {
                    this.vendorBankAccount.setValue(this.vendorBankAccounts[0].id);
                }
            } else {
                this.vendorBankAccount.clearValidators();
                this.vendorBankAccount.disable();
                this.vendorBankAccount.setValue(null);
            }

            this.bankAccount.updateValueAndValidity();
            this.paymentForm.get('checkNumber').updateValueAndValidity();
            this.vendorBankAccount.updateValueAndValidity();
        });
    }

    private clearBankAccountAutoSelectTimeout(): void {
        if (this.bankAccountAutoSelectTimeout !== null) {
            clearTimeout(this.bankAccountAutoSelectTimeout);
            this.bankAccountAutoSelectTimeout = null;
        }
    }
}
